import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertTriangle, CheckCircle2, Download, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import { priceText, usd } from '@/lib/reports/format';
import { useReconciliation, useReconciliationDownload } from '@/lib/reconciliation/use-reconciliation';
import type { ReconciliationReport, ReconciliationRow } from '@/lib/reconciliation/types';

// Disruption control-center Reconciliation tab (#807, SKY PoC §1/§8). One row per
// passenger of the manifest with what the policy expected vs what the platform
// executed per service, the totals, whether the hotel figures reconcile with the
// disruption's hotel headline, and the Excel-ready CSV download. Unknown upstream
// reads are said so (never shown as zero). Every amount is the airline's price
// (commercial-contract M3, converted by the BFF; meals at card price); without a price
// card the amounts read «No disponible» and a notice says why.

interface ReconciliationTabProps {
  disruptionUrn: string;
  enabled: boolean;
}

type Filter = 'all' | 'differences' | 'noHotel' | 'excluded';

const FILTERS: Filter[] = ['all', 'differences', 'noHotel', 'excluded'];

/** Live reservation statuses — the rows whose hotel would carry a price. */
const LIVE_HOTEL = new Set(['CONFIRMED', 'HELD', 'PENDING']);

function matches(row: ReconciliationRow, filter: Filter, needle: string): boolean {
  if (filter === 'differences' && row.differences.length === 0) return false;
  if (filter === 'noHotel' && !['NONE', 'FAILED', 'CANCELLED'].includes(row.hotel.status)) return false;
  if (filter === 'excluded' && !row.excluded) return false;
  if (!needle) return true;
  const hay = `${row.pnr} ${row.passengerName}`.toLowerCase();
  return hay.includes(needle);
}

export function ReconciliationTab({ disruptionUrn, enabled }: ReconciliationTabProps) {
  const { t, i18n } = useTranslation('disruptions');
  const { data, isLoading, isError } = useReconciliation(disruptionUrn, enabled);
  const download = useReconciliationDownload(disruptionUrn);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (data?.rows ?? []).filter((r) => matches(r, filter, needle));
  }, [data, filter, search]);

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="reconciliation-loading">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">{t('reconciliation.error')}</CardContent>
      </Card>
    );
  }

  const unknownSources = (['booking', 'transport', 'notifications', 'spend'] as const).filter((k) => !data.known[k]);

  return (
    <div className="flex flex-col gap-4" data-testid="reconciliation-tab">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t('reconciliation.title')}</h2>
          <p className="text-sm text-muted-foreground">
            {t('reconciliation.subtitle', { when: new Date(data.generatedAt).toLocaleString(i18n.language) })}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="reconciliation-price-note">
            {t('reconciliation.priceNote')}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => download.mutate()}
          disabled={download.isPending}
          data-testid="reconciliation-download"
        >
          {download.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden /> : <Download className="mr-1 h-4 w-4" aria-hidden />}
          {download.isPending ? t('reconciliation.downloading') : t('reconciliation.download')}
        </Button>
      </div>

      {!data.priced ? (
        <p className="flex items-center gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300" role="status" data-testid="reconciliation-unpriced">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          {t('operateCancel:spend.unpricedHint')}
        </p>
      ) : null}

      <Totals report={data} t={t} />

      {unknownSources.length > 0 ? (
        <p className="flex items-center gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300" data-testid="reconciliation-unknown">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          {t('reconciliation.unknown.hint', { sources: unknownSources.map((k) => t(`reconciliation.unknown.${k}`)).join(', ') })}
        </p>
      ) : null}

      {data.rows.length === 0 ? (
        <EmptyState title={t('reconciliation.empty.title')} description={t('reconciliation.empty.description')} />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1" role="tablist" aria-label={t('reconciliation.filter.all')}>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  role="tab"
                  aria-selected={filter === f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-xs',
                    filter === f ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                  data-testid={`reconciliation-filter-${f}`}
                >
                  {t(`reconciliation.filter.${f}`)}
                  {f === 'differences' ? ` (${data.totals.withDifferences})` : ''}
                  {f === 'excluded' ? ` (${data.totals.excluded})` : ''}
                </button>
              ))}
            </div>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('reconciliation.filter.search')}
              aria-label={t('reconciliation.filter.search')}
              className="h-8 w-full rounded-md border border-border bg-background px-2 text-sm sm:ml-auto sm:w-56"
              data-testid="reconciliation-search"
            />
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full min-w-[64rem] text-sm" data-testid="reconciliation-table">
              <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.pnr')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.passenger')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.condition')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.flight')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.hotel')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.transport')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.meal')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.notifications')}</th>
                  <th className="px-3 py-2 font-medium">{t('reconciliation.col.differences')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.paxUrn} className={cn('border-t border-border align-top', row.excluded && 'text-muted-foreground')} data-testid="reconciliation-row">
                    <td className="px-3 py-2 font-mono font-medium">
                      {row.pnr}
                      {row.partyRootPnr ? <div className="text-xs font-sans text-muted-foreground">↳ {row.partyRootPnr}</div> : null}
                    </td>
                    <td className="px-3 py-2">
                      {row.passengerName || '—'}
                      <div className="text-xs text-muted-foreground">
                        {row.paxType}
                        {row.excluded ? ` · ${t('reconciliation.filter.excluded')}` : ''}
                      </div>
                    </td>
                    <td className="px-3 py-2">{row.condition ?? '—'}</td>
                    <td className="px-3 py-2">
                      {row.flight.assigned ?? '—'}
                      <div className="text-xs text-muted-foreground">{t(`reconciliation.rebookingSource.${row.rebookingSource}`)}</div>
                    </td>
                    <td className="px-3 py-2">
                      <HotelCell row={row} priced={data.priced} t={t} />
                    </td>
                    <td className="px-3 py-2">
                      <TransportCell row={row} priced={data.priced} t={t} />
                    </td>
                    <td className="px-3 py-2">
                      <MealCell row={row} priced={data.priced} t={t} />
                    </td>
                    <td className="px-3 py-2">
                      {row.notifications.channels.length > 0 ? row.notifications.channels.join(' ') : '—'}
                      <div className="text-xs text-muted-foreground">{row.notifications.lastState ?? '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      {row.differences.length === 0 ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="ok" />
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {row.differences.map((d) => (
                            <Badge key={d} variant="warning" size="sm" data-testid="reconciliation-difference">
                              {t(`reconciliation.difference.${d}`)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Totals({ report, t }: { report: ReconciliationReport; t: TFunction<'disruptions'> }) {
  const { totals } = report;
  const price = (v: number | null) => priceText(v, report.priced, t('operateCancel:spend.unpriced'));
  const tiles: Array<{ key: string; label: string; value: string; hint?: string }> = [
    { key: 'passengers', label: t('reconciliation.totals.passengers'), value: String(totals.passengers), hint: t('reconciliation.totals.excluded', { n: totals.excluded }) },
    { key: 'hotelPrice', label: t('reconciliation.totals.hotelPrice'), value: price(totals.hotel.priceUsd), hint: `${totals.hotel.confirmed} ✓ · ${totals.hotel.held} ⏳ · ${totals.hotel.failed} ✕` },
    { key: 'hotelEstimate', label: t('reconciliation.totals.hotelEstimate'), value: price(totals.hotel.estimatedUsd) },
    { key: 'transportPrice', label: t('reconciliation.totals.transportPrice'), value: price(totals.transport.priceUsd), hint: `${totals.transport.withOutbound} / ${totals.transport.expected}` },
    { key: 'mealIssued', label: t('reconciliation.totals.mealIssued'), value: price(totals.meal.issuedUsd), hint: `${totals.meal.issued} / ${totals.meal.expected}` },
    { key: 'mealRedeemed', label: t('reconciliation.totals.mealRedeemed'), value: String(totals.meal.redeemed ?? 0), hint: `/ ${totals.meal.issued}` },
    { key: 'differences', label: t('reconciliation.totals.differences'), value: String(totals.withDifferences) },
  ];
  const reconciled =
    report.reconciledWithSummary === null
      ? t('reconciliation.reconciled.unknown')
      : report.reconciledWithSummary
        ? t('reconciliation.reconciled.ok', { amount: usd(report.summaryHotelSpendUsd) })
        : t('reconciliation.reconciled.mismatch', { amount: usd(report.summaryHotelSpendUsd) });
  return (
    <div className="space-y-2" data-testid="reconciliation-totals">
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {tiles.map((tile) => (
          <div key={tile.key} className="rounded-md border border-border p-3" data-testid={`reconciliation-total-${tile.key}`}>
            <dt className="text-xs uppercase text-muted-foreground">{tile.label}</dt>
            <dd className="text-xl font-semibold tabular-nums">{tile.value}</dd>
            {tile.hint ? <dd className="text-xs text-muted-foreground">{tile.hint}</dd> : null}
          </div>
        ))}
      </dl>
      <p
        className={cn('text-xs', report.reconciledWithSummary === false ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground')}
        data-testid="reconciliation-reconciled"
      >
        {reconciled}
      </p>
    </div>
  );
}

function HotelCell({ row, priced, t }: { row: ReconciliationRow; priced: boolean; t: TFunction<'disruptions'> }) {
  const expected =
    row.hotel.expected === null ? t('reconciliation.expected.undecided') : row.hotel.expected ? t('reconciliation.expected.yes') : t('reconciliation.expected.no');
  // Unpriced: a live reservation still has a price — it just is not available right now.
  const amount =
    row.hotel.priceUsd !== null
      ? usd(row.hotel.priceUsd)
      : row.hotel.estimatedUsd !== null
        ? `~${usd(row.hotel.estimatedUsd)}`
        : !priced && LIVE_HOTEL.has(row.hotel.status)
          ? t('operateCancel:spend.unpriced')
          : expected;
  return (
    <>
      <div>
        {row.hotel.status === 'SELF_MANAGED' && row.hotel.source === 'PASSENGER'
          ? t('reconciliation.hotelStatus.SELF_MANAGED_PASSENGER')
          : t(`reconciliation.hotelStatus.${row.hotel.status}`)}
      </div>
      <div className="text-xs text-muted-foreground">
        {row.hotel.hotelName ? `${row.hotel.hotelName}${row.hotel.vendor ? ` (${row.hotel.vendor})` : ''} · ` : ''}
        {row.hotel.nights !== null ? `${row.hotel.nights}n · ` : ''}
        {amount}
      </div>
    </>
  );
}

function tripLabel(trip: ReconciliationRow['transport']['outbound'], priced: boolean, unavailable: string): string {
  if (!trip) return '—';
  const amount = trip.priceUsd !== null ? ` ${usd(trip.priceUsd)}` : priced ? '' : ` · ${unavailable}`;
  return `${trip.status}${trip.vehicleClass ?? trip.kind ? ` ${trip.vehicleClass ?? trip.kind}` : ''}${amount}`;
}

function TransportCell({ row, priced, t }: { row: ReconciliationRow; priced: boolean; t: TFunction<'disruptions'> }) {
  const unavailable = t('operateCancel:spend.unpriced');
  if (row.transport.outcome?.toUpperCase() === 'OWN_MEANS') return <span>{t('reconciliation.transport.ownMeans')}</span>;
  if (!row.transport.outbound && !row.transport.inbound) {
    return <span className="text-muted-foreground">{t('reconciliation.transport.none')}</span>;
  }
  return (
    <>
      <div>
        {t('reconciliation.transport.outbound')}: {tripLabel(row.transport.outbound, priced, unavailable)}
      </div>
      <div className="text-xs text-muted-foreground">
        {t('reconciliation.transport.inbound')}: {tripLabel(row.transport.inbound, priced, unavailable)}
      </div>
    </>
  );
}

function MealCell({ row, priced, t }: { row: ReconciliationRow; priced: boolean; t: TFunction<'disruptions'> }) {
  // `meal.expected` (not the amount) says who was owed a meal — the amount is null without a price card.
  if (!row.meal.expected && !row.meal.issued) return <span className="text-muted-foreground">{t('reconciliation.meal.none')}</span>;
  const price = (v: number | null) => priceText(v, priced, t('operateCancel:spend.unpriced'));
  return (
    <>
      <div>
        {row.meal.issued ? t('reconciliation.meal.issued', { amount: price(row.meal.issuedUsd) }) : t('reconciliation.meal.notIssued')}
        {row.meal.redeemed === true ? (
          <span className="ml-1.5 rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" data-testid="reconciliation-meal-redeemed">
            {row.meal.redemptionSource === 'ISSUER' ? t('reconciliation.meal.redeemedIssuer') : t('reconciliation.meal.redeemedSimulated')}
          </span>
        ) : row.meal.redeemed === false ? (
          <span className="ml-1.5 text-[11px] text-muted-foreground" data-testid="reconciliation-meal-not-redeemed">{t('reconciliation.meal.notRedeemed')}</span>
        ) : null}
      </div>
      <div className="text-xs text-muted-foreground">
        {t('reconciliation.meal.expected', { amount: price(row.meal.expectedUsd) })}
        {row.meal.toppedUpUsd ? ` · ${t('reconciliation.meal.topUp', { amount: usd(row.meal.toppedUpUsd) })}` : ''}
      </div>
    </>
  );
}
